import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { NbAuthJWTToken } from '@nebular/auth';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { TokenRefreshService, hasuraAuthHeaders } from './token-refresh.service';
import { SKIP_TOKEN_REFRESH } from './expofin-auth.strategy';
import { RateLimitNotifier } from './rate-limit';

/**
 * Before every request to our backend (REST under api_path and Hasura) makes sure the access
 * token is valid, refreshing it if needed. A 401 answer triggers one forced refresh and a retry.
 * Auth endpoints (/api/auth/*) are left untouched: login, refresh and logout must not loop.
 * Rate-limit answers (429) are shown to the user and never retried.
 */
@Injectable()
export class RefreshInterceptor implements HttpInterceptor {

  constructor(private tokens: TokenRefreshService, private rateLimit: RateLimitNotifier) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.context.get(SKIP_TOKEN_REFRESH) || !this.isBackend(request.url) || request.url.includes('/api/auth/')) {
      return next.handle(request).pipe(catchError(error => this.rejectRateLimited(error)));
    }

    return from(this.tokens.getValidToken()).pipe(
      switchMap(token => next.handle(this.withToken(request, token))),
      catchError(error => {
        // 429 is shown to the user and never retried.
        if (this.rateLimit.handle(error)) return throwError(() => error);
        if (!(error instanceof HttpErrorResponse) || error.status !== 401) return throwError(() => error);
        return from(this.tokens.getValidToken(true, `401 ${request.method} ${request.url}`)).pipe(
          switchMap(token => token ? next.handle(this.withToken(request, token)) : throwError(() => error)),
          catchError(retryError => this.rejectRateLimited(retryError)),
        );
      }),
    );
  }

  private rejectRateLimited(error: any): Observable<never> {
    this.rateLimit.handle(error);
    return throwError(() => error);
  }

  private isBackend(url: string): boolean {
    return url.startsWith(environment.hasura_uri) || url.startsWith(environment.api_path) || url.startsWith(environment.auth_path) || url.startsWith('/api/');
  }

  /** Puts the current token on Hasura calls, and replaces it on REST calls that already send one. */
  private withToken(request: HttpRequest<unknown>, token: NbAuthJWTToken | null): HttpRequest<unknown> {
    if (!token) return request;
    if (request.url.startsWith(environment.hasura_uri)) return request.clone({ setHeaders: hasuraAuthHeaders(token) });
    if (request.headers.has('Authorization')) {
      return request.clone({ setHeaders: { Authorization: `Bearer ${token.getValue()}` } });
    }
    return request;
  }
}
